import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowRight,
  Building2,
  Clock,
  CloudOff,
  Hourglass,
  Network as NetworkIcon,
  Pencil,
  Plus,
  Trash2,
  UserPlus,
  X,
} from 'lucide-react'
import { networksApi } from '@/api/networksApi'
import { rolesApi, type RoleListItem } from '@/api/rolesApi'
import { tenantsApi, type TenantMember } from '@/api/tenantsApi'
import type { OfflineSignatureMode } from '@/api/syncApi'
import { usersApi } from '@/api/usersApi'
import { usePermission, PERMISSIONS } from '@/lib/permissions'
import { CopyButton } from '@/components/integrations/WebhooksPanel'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

/**
 * Permanent tenancy management: the tenants of this instance (create / rename /
 * status / delete-with-pre-checks) plus a members panel for the selected tenant.
 *
 * The network is shown as a heading, not a selector — each network gets its own
 * deployment, so this instance has exactly one and there is nothing to choose
 * between. Its name is renameable here because APP_INSTANCE_NAME is only read
 * while the Network table is empty, so this screen is the sole way to correct it
 * after the first boot. Deletes are refused server-side (409 with per-table
 * counts) while data still references the tenant; the error is surfaced verbatim
 * so the admin sees what blocks it.
 */

type Selection = { id: string; name: string } | null

// The api helper throws `API DELETE /… failed (409): {json}` — pull the
// blockers out of that JSON tail when present so the message reads clean.
function describeBlockers(message: string): string {
  const jsonStart = message.indexOf('): ')
  if (jsonStart === -1) return message
  try {
    const parsed = JSON.parse(message.slice(jsonStart + 3)) as {
      message?: string
      blockers?: { table: string; count: number }[]
    }
    if (parsed.blockers?.length) {
      const detail = parsed.blockers.map((b) => `${b.count} ${b.table}`).join(', ')
      return `${parsed.message ?? 'Cannot delete'}: ${detail}`
    }
    return parsed.message ?? message
  } catch {
    return message
  }
}

export function TenantsSettings() {
  const qc = useQueryClient()
  const { can, isSuperAdmin } = usePermission()
  // Tenant CRUD hits global routes -> superadmin only. Member management hits
  // /tenants/:tenantId/members*, which IS tenant-scoped, so a tenant admin
  // holding users.manage in that tenant can run it.
  const canTenants = isSuperAdmin
  const canMembers = isSuperAdmin || can(PERMISSIONS.usersManage)

  const instance = useQuery({ queryKey: ['instance-network'], queryFn: networksApi.current })
  const tenants = useQuery({ queryKey: ['tenants'], queryFn: tenantsApi.list })
  // For the superadmin member picker. Admin pages are low-traffic; 200 covers
  // current orgs.
  //
  // Gated on isSuperAdmin, NOT canMembers: GET /users is a global route, so a
  // tenant admin would 403 here. That failure would be invisible (this query's
  // error is never rendered) and would leave them with a silently empty picker,
  // which is why they get the by-email control instead.
  const users = useQuery({
    queryKey: ['users', 0, 200],
    queryFn: () => usersApi.list({ skip: 0, take: 200 }),
    enabled: isSuperAdmin,
  })
  // Readable by any signed-in user, so tenant admins can fill the role picker.
  const roles = useQuery({ queryKey: ['roles'], queryFn: rolesApi.list, enabled: canMembers })

  const [selection, setSelection] = useState<Selection>(null)
  const [error, setError] = useState<string | null>(null)
  const [rename, setRename] = useState<{ id: string; name: string } | null>(null)
  const [editingTimezone, setEditingTimezone] = useState<{ id: string; name: string; timezone: string } | null>(
    null,
  )
  const [editingGrace, setEditingGrace] = useState<{ id: string; name: string; days: number } | null>(null)
  const [editingOffline, setEditingOffline] = useState<{
    id: string
    name: string
    hours: number
    signatureMode: OfflineSignatureMode
  } | null>(null)
  const [renamingNetwork, setRenamingNetwork] = useState(false)
  const [creatingTenant, setCreatingTenant] = useState(false)

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['tenants'] })
    void qc.invalidateQueries({ queryKey: ['users'], exact: false })
  }
  const onErr = (e: unknown) =>
    setError(e instanceof Error ? describeBlockers(e.message) : 'Action failed')

  const createTenant = useMutation({
    mutationFn: (name: string) => tenantsApi.create({ name }),
    onSuccess: () => {
      setCreatingTenant(false)
      setError(null)
      invalidate()
    },
    onError: onErr,
  })
  const updateTenant = useMutation({
    mutationFn: (i: {
      id: string
      name?: string
      status?: 'active' | 'inactive'
      timezone?: string
      maintenanceTaskOverdueGraceDays?: number
      offlineWindowHours?: number
      offlineSignatureMode?: OfflineSignatureMode
    }) =>
      tenantsApi.update(i.id, {
        name: i.name,
        status: i.status,
        timezone: i.timezone,
        maintenanceTaskOverdueGraceDays: i.maintenanceTaskOverdueGraceDays,
        offlineWindowHours: i.offlineWindowHours,
        offlineSignatureMode: i.offlineSignatureMode,
      }),
    onSuccess: () => {
      setRename(null)
      setEditingTimezone(null)
      setEditingGrace(null)
      setEditingOffline(null)
      setError(null)
      invalidate()
      // Timezone and grace both decide which maintenance tasks are overdue.
      void qc.invalidateQueries({ queryKey: ['maintenance-events'] })
    },
    onError: onErr,
  })
  // Invalidating ['instance-network'] refreshes both readers at once: this
  // heading and the top bar (via TenantProvider).
  const updateNetwork = useMutation({
    mutationFn: (name: string) => networksApi.update({ name }),
    onSuccess: () => {
      setRenamingNetwork(false)
      setError(null)
      void qc.invalidateQueries({ queryKey: ['instance-network'] })
    },
    onError: onErr,
  })
  const removeTenant = useMutation({
    mutationFn: tenantsApi.remove,
    onSuccess: () => {
      setSelection(null)
      setError(null)
      invalidate()
    },
    onError: onErr,
  })

  const tenantList = tenants.data ?? []

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-start justify-between gap-4'>
        <p className='text-sm text-muted-foreground'>
          {canTenants ? (
            <>
              Tenants are the hospitals on this instance. Deleting is only possible once nothing
              references the tenant.
            </>
          ) : (
            <>
              The hospitals you belong to. Select one to manage who works there and what they can
              do.
            </>
          )}
        </p>
        {canTenants && (
          <Link
            to='/tenancy-setup'
            className='flex shrink-0 items-center gap-1 text-sm font-medium text-primary transition-colors hover:underline'
            title='One-time adoption: assign data that predates tenancy to a tenant'
          >
            Tenancy setup
            <ArrowRight className='h-3.5 w-3.5' />
          </Link>
        )}
      </div>

      {/* The network every tenant below belongs to. One per deployment, so it is
          a label rather than a picker. */}
      <div className='flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2'>
        <NetworkIcon className='h-4 w-4 shrink-0 text-muted-foreground' />
        <span className='text-xs uppercase tracking-wide text-muted-foreground'>Network</span>
        <span className='min-w-0 truncate text-sm font-medium'>
          {instance.data?.name ?? (instance.isLoading ? 'Loading…' : '—')}
        </span>
        {canTenants && instance.data && (
          <Button
            variant='ghost'
            size='icon'
            className='ml-auto h-7 w-7'
            title='Rename network'
            onClick={() => setRenamingNetwork(true)}
          >
            <Pencil className='h-3.5 w-3.5' />
          </Button>
        )}
      </div>

      {error && (
        <p className='text-sm text-destructive' role='alert'>
          {error}
        </p>
      )}

      <div className='grid gap-4 lg:grid-cols-[1fr_minmax(20rem,24rem)]'>
        <Card>
          <CardContent className='flex flex-col gap-2 p-4'>
            {canTenants && (
              <div className='flex justify-end pb-1'>
                <Button size='sm' onClick={() => setCreatingTenant(true)}>
                  <Plus className='mr-1 h-3.5 w-3.5' /> Tenant
                </Button>
              </div>
            )}

            {tenants.isLoading && <p className='text-sm text-muted-foreground'>Loading…</p>}
            {tenantList.map((t) => (
              <div
                key={t.id}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${
                  selection?.id === t.id ? 'bg-muted/60' : ''
                }`}
              >
                <button
                  type='button'
                  className='flex flex-1 items-center gap-2 text-left text-sm font-medium'
                  title='Show members'
                  onClick={() => setSelection({ id: t.id, name: t.name })}
                >
                  <Building2 className='h-4 w-4 shrink-0 text-muted-foreground' />
                  <span className='flex min-w-0 flex-col'>
                    <span className='flex items-center gap-2'>
                      {t.name}
                      {t.status === 'inactive' && <Badge variant='secondary'>inactive</Badge>}
                      {t.offlineSignatureMode === 'ON' && <Badge variant='secondary'>offline signing</Badge>}
                    </span>
                    {/* The id other systems use for this site (e.g. Smart Support's "CGT tenant id"). */}
                    <span className='truncate font-mono text-[11px] font-normal text-muted-foreground'>{t.id}</span>
                  </span>
                </button>
                <CopyButton value={t.id} label={`Copy tenant id of ${t.name}`} />
                {canTenants && (
                  <>
                    <Switch
                      checked={t.status === 'active'}
                      title='Active / inactive'
                      onCheckedChange={(active) =>
                        updateTenant.mutate({ id: t.id, status: active ? 'active' : 'inactive' })
                      }
                    />
                    <Button
                      variant='ghost'
                      size='icon'
                      title='Rename tenant'
                      onClick={() => setRename({ id: t.id, name: t.name })}
                    >
                      <Pencil className='h-3.5 w-3.5' />
                    </Button>
                    <Button
                      variant='ghost'
                      size='icon'
                      title="Timezone (sets today's date for calculated fields and for overdue maintenance tasks)"
                      onClick={() => setEditingTimezone({ id: t.id, name: t.name, timezone: t.timezone })}
                    >
                      <Clock className='h-3.5 w-3.5' />
                    </Button>
                    <Button
                      variant='ghost'
                      size='icon'
                      title='Maintenance task grace days'
                      onClick={() =>
                        setEditingGrace({ id: t.id, name: t.name, days: t.maintenanceTaskOverdueGraceDays ?? 0 })
                      }
                    >
                      <Hourglass className='h-3.5 w-3.5' />
                    </Button>
                    <Button
                      variant='ghost'
                      size='icon'
                      title={`Offline window for the mobile app: ${t.offlineWindowHours ?? 12} h. Offline signing on phones: ${
                        t.offlineSignatureMode === 'ON' ? 'On' : 'Off'
                      }`}
                      onClick={() =>
                        setEditingOffline({
                          id: t.id,
                          name: t.name,
                          hours: t.offlineWindowHours ?? 12,
                          signatureMode: t.offlineSignatureMode ?? 'OFF',
                        })
                      }
                    >
                      <CloudOff className='h-3.5 w-3.5' />
                    </Button>
                    <Button
                      variant='ghost'
                      size='icon'
                      title='Delete tenant (refused while data references it)'
                      disabled={removeTenant.isPending}
                      onClick={() => {
                        if (window.confirm(`Delete tenant "${t.name}"?`)) {
                          setError(null)
                          removeTenant.mutate(t.id)
                        }
                      }}
                    >
                      <Trash2 className='h-3.5 w-3.5 text-destructive' />
                    </Button>
                  </>
                )}
              </div>
            ))}
            {tenantList.length === 0 && !tenants.isLoading && (
              <p className='text-sm text-muted-foreground'>
                {canTenants
                  ? 'No tenants yet — create one above.'
                  : 'You are not a member of any tenant yet.'}
              </p>
            )}
          </CardContent>
        </Card>

        <MembersPanel
          selection={selection}
          canMembers={canMembers}
          isSuperAdmin={isSuperAdmin}
          roles={roles.data ?? []}
          allUsers={(users.data?.items ?? []).map((u) => ({
            value: u.id,
            label: u.fullName ? `${u.fullName} (${u.email})` : u.email,
          }))}
          onError={onErr}
          clearError={() => setError(null)}
        />
      </div>

      {rename && (
        <RenameDialog
          title='Rename tenant'
          initial={rename.name}
          pending={updateTenant.isPending}
          onClose={() => setRename(null)}
          onSave={(name) => updateTenant.mutate({ id: rename.id, name })}
        />
      )}

      {editingTimezone && (
        <TimezoneDialog
          tenantName={editingTimezone.name}
          initial={editingTimezone.timezone}
          pending={updateTenant.isPending}
          onClose={() => setEditingTimezone(null)}
          onSave={(timezone) => updateTenant.mutate({ id: editingTimezone.id, timezone })}
        />
      )}

      {editingGrace && (
        <GraceDaysDialog
          tenantName={editingGrace.name}
          initial={editingGrace.days}
          pending={updateTenant.isPending}
          onClose={() => setEditingGrace(null)}
          onSave={(days) =>
            updateTenant.mutate({ id: editingGrace.id, maintenanceTaskOverdueGraceDays: days })
          }
        />
      )}

      {editingOffline && (
        <OfflineWindowDialog
          tenantName={editingOffline.name}
          initial={editingOffline.hours}
          initialSignatureMode={editingOffline.signatureMode}
          pending={updateTenant.isPending}
          onClose={() => setEditingOffline(null)}
          onSave={(hours, signatureMode) =>
            updateTenant.mutate({
              id: editingOffline.id,
              offlineWindowHours: hours,
              offlineSignatureMode: signatureMode,
            })
          }
        />
      )}

      {renamingNetwork && instance.data && (
        <RenameDialog
          title='Rename network'
          initial={instance.data.name}
          pending={updateNetwork.isPending}
          onClose={() => setRenamingNetwork(false)}
          onSave={(name) => updateNetwork.mutate(name)}
        />
      )}

      {creatingTenant && (
        <NewTenantDialog
          instanceName={instance.data?.name ?? null}
          pending={createTenant.isPending}
          onClose={() => setCreatingTenant(false)}
          onSave={(name) => createTenant.mutate(name)}
        />
      )}
    </div>
  )
}

const NO_ROLE = '__none__'

/**
 * The delegable half of this screen. `users.manage` here resolves against the
 * caller's role in the SELECTED tenant, because /tenants/:tenantId/members*
 * carries the tenant in the URL — so a tenant admin runs this without being a
 * platform superadmin.
 *
 * Adding is the one asymmetric action. A superadmin picks from the full user
 * directory (GET /users); a tenant admin cannot, because that route is global,
 * so they name the person by email. Same mutation shape, different lookup.
 */
function MembersPanel({
  selection,
  canMembers,
  isSuperAdmin,
  roles,
  allUsers,
  onError,
  clearError,
}: {
  selection: Selection
  canMembers: boolean
  isSuperAdmin: boolean
  roles: RoleListItem[]
  allUsers: { value: string; label: string }[]
  onError: (e: unknown) => void
  clearError: () => void
}) {
  const qc = useQueryClient()
  const [pickedUserId, setPickedUserId] = useState('')
  const [email, setEmail] = useState('')
  const [pickedRoleId, setPickedRoleId] = useState(NO_ROLE)
  // Revealed only after an add-by-email 404, never as a fallback: a mistyped
  // address must not silently provision a real account that then squats it.
  const [creating, setCreating] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [tempPassword, setTempPassword] = useState('')

  const members = useQuery({
    queryKey: ['members', 'tenant', selection?.id],
    queryFn: () => tenantsApi.listMembers(selection?.id ?? ''),
    enabled: selection !== null && canMembers,
  })

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['members'], exact: false })
    void qc.invalidateQueries({ queryKey: ['users'], exact: false })
  }

  const addMember = useMutation({
    mutationFn: (member: { userId: string; roleId?: string }) =>
      tenantsApi.addMembers(selection?.id ?? '', [member]),
    onSuccess: () => {
      setPickedUserId('')
      setPickedRoleId(NO_ROLE)
      clearError()
      invalidate()
    },
    onError,
  })
  const resetAdd = () => {
    setEmail('')
    setPickedRoleId(NO_ROLE)
    setCreating(false)
    setFirstName('')
    setLastName('')
    setTempPassword('')
  }

  const addMemberByEmail = useMutation({
    mutationFn: (input: { email: string; roleId?: string }) =>
      tenantsApi.addMemberByEmail(selection?.id ?? '', input),
    onSuccess: () => {
      resetAdd()
      clearError()
      invalidate()
    },
    onError,
  })
  const createMember = useMutation({
    mutationFn: (input: {
      email: string
      firstName: string
      lastName: string
      temporaryPassword: string
      roleId?: string
    }) => tenantsApi.createMember(selection?.id ?? '', input),
    onSuccess: () => {
      resetAdd()
      clearError()
      invalidate()
    },
    onError,
  })

  // The api helper throws `API POST /… failed (404): {json}`, so the status is
  // the only thing distinguishing "no such account" (offer to create one) from
  // any other failure (just report it).
  const emailNotFound =
    addMemberByEmail.isError && /failed \(404\)/.test((addMemberByEmail.error as Error).message)
  const setMemberRole = useMutation({
    mutationFn: (i: { userId: string; roleId: string | null }) =>
      tenantsApi.setMemberRole(selection?.id ?? '', i.userId, i.roleId),
    onSuccess: () => {
      clearError()
      invalidate()
    },
    onError,
  })
  const removeMember = useMutation({
    mutationFn: (userId: string) => tenantsApi.removeMember(selection?.id ?? '', userId),
    onSuccess: () => {
      clearError()
      invalidate()
    },
    onError,
  })

  if (!selection) {
    return (
      <Card>
        <CardContent className='p-4'>
          <p className='text-sm text-muted-foreground'>Select a tenant to manage its members.</p>
        </CardContent>
      </Card>
    )
  }

  const memberUserIds = new Set((members.data ?? []).map((m: TenantMember) => m.user.id))
  const addable = allUsers.filter((u) => !memberUserIds.has(u.value))

  return (
    <Card>
      <CardContent className='flex flex-col gap-3 p-4'>
        <p className='text-sm font-medium'>Members — {selection.name}</p>

        {!canMembers && (
          <p className='text-xs text-muted-foreground'>
            You need user management permission to view members.
          </p>
        )}

        {canMembers && (
          <>
            {members.isLoading && <p className='text-sm text-muted-foreground'>Loading…</p>}
            {(members.data ?? []).map((m: TenantMember) => (
              <div key={m.id} className='flex items-center gap-2 rounded-md border px-2.5 py-1.5'>
                <div className='min-w-0 flex-1'>
                  <p className='truncate text-sm'>{m.user.fullName ?? m.user.email}</p>
                  <p className='truncate text-xs text-muted-foreground'>
                    {m.user.email} · added by {m.createdBy}
                  </p>
                </div>
                <Select
                  value={m.roleId ?? NO_ROLE}
                  disabled={setMemberRole.isPending}
                  onValueChange={(roleId) =>
                    setMemberRole.mutate({
                      userId: m.user.id,
                      roleId: roleId === NO_ROLE ? null : roleId,
                    })
                  }
                >
                  <SelectTrigger
                    className='h-7 w-36 text-xs'
                    title='Role in this tenant'
                  >
                    <SelectValue placeholder='No role' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_ROLE}>No role</SelectItem>
                    {roles.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant='ghost'
                  size='icon'
                  title='Remove member'
                  disabled={removeMember.isPending}
                  onClick={() => removeMember.mutate(m.user.id)}
                >
                  <X className='h-3.5 w-3.5' />
                </Button>
              </div>
            ))}
            {members.data?.length === 0 && (
              <p className='text-xs text-muted-foreground'>No members yet.</p>
            )}

            <div className='flex flex-col gap-2 pt-1'>
              {/* Superadmins pick from the directory; tenant admins cannot read
                  it (GET /users is global), so they name the person by email. */}
              {isSuperAdmin ? (
                <SearchableSelect
                  value={pickedUserId}
                  onChange={setPickedUserId}
                  options={addable}
                  placeholder='Add a user…'
                />
              ) : (
                <Input
                  type='email'
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    // The 404 belongs to the address that produced it. Editing
                    // it must retract both the offer and any open create form,
                    // or the fields would submit against a different email.
                    if (addMemberByEmail.isError) addMemberByEmail.reset()
                    setCreating(false)
                  }}
                  placeholder='Add by email…'
                  className='h-8 text-xs'
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && email.trim() && !addMemberByEmail.isPending) {
                      addMemberByEmail.mutate({
                        email: email.trim(),
                        ...(pickedRoleId === NO_ROLE ? {} : { roleId: pickedRoleId }),
                      })
                    }
                  }}
                />
              )}
              <div className='flex items-center gap-2'>
                <Select value={pickedRoleId} onValueChange={setPickedRoleId}>
                  <SelectTrigger className='h-8 flex-1 text-xs' title='Role in this tenant'>
                    <SelectValue placeholder='No role' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_ROLE}>No role</SelectItem>
                    {roles.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isSuperAdmin ? (
                  <Button
                    size='sm'
                    disabled={!pickedUserId || addMember.isPending}
                    onClick={() =>
                      addMember.mutate({
                        userId: pickedUserId,
                        ...(pickedRoleId === NO_ROLE ? {} : { roleId: pickedRoleId }),
                      })
                    }
                  >
                    <UserPlus className='mr-1 h-3.5 w-3.5' /> Add
                  </Button>
                ) : (
                  <Button
                    size='sm'
                    disabled={!email.trim() || addMemberByEmail.isPending}
                    onClick={() =>
                      addMemberByEmail.mutate({
                        email: email.trim(),
                        ...(pickedRoleId === NO_ROLE ? {} : { roleId: pickedRoleId }),
                      })
                    }
                  >
                    <UserPlus className='mr-1 h-3.5 w-3.5' /> Add
                  </Button>
                )}
              </div>
              {!isSuperAdmin && emailNotFound && !creating && (
                <div className='rounded-md border border-dashed px-2.5 py-2'>
                  <p className='text-xs text-muted-foreground'>
                    No account for {email.trim()}.
                  </p>
                  <Button
                    variant='outline'
                    size='sm'
                    className='mt-1.5 h-7 text-xs'
                    onClick={() => setCreating(true)}
                  >
                    <UserPlus className='mr-1 h-3.5 w-3.5' /> Create an account for this address
                  </Button>
                </div>
              )}

              {!isSuperAdmin && creating && (
                <div className='flex flex-col gap-2 rounded-md border px-2.5 py-2'>
                  <p className='text-xs font-medium'>New account for {email.trim()}</p>
                  {/* Required, like every other way an account is made — the
                      backend refuses a nameless user whichever route mints it. */}
                  <div className='grid grid-cols-2 gap-2'>
                    <Input
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder='First name'
                      className='h-8 text-xs'
                    />
                    <Input
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder='Last name'
                      className='h-8 text-xs'
                    />
                  </div>
                  <Input
                    type='password'
                    value={tempPassword}
                    onChange={(e) => setTempPassword(e.target.value)}
                    placeholder='Password'
                    className='h-8 text-xs'
                  />
                  <p className='text-xs text-muted-foreground'>
                    At least 8 characters with upper, lower and a number. No email is sent, so pass
                    it on yourself — they sign in with it as it is. They join with the role
                    selected above and get no access anywhere else.
                  </p>
                  <div className='flex items-center gap-2'>
                    <Button
                      size='sm'
                      className='h-7 text-xs'
                      disabled={
                        tempPassword.length < 8 ||
                        !firstName.trim() ||
                        !lastName.trim() ||
                        createMember.isPending
                      }
                      onClick={() =>
                        createMember.mutate({
                          email: email.trim(),
                          firstName: firstName.trim(),
                          lastName: lastName.trim(),
                          temporaryPassword: tempPassword,
                          ...(pickedRoleId === NO_ROLE ? {} : { roleId: pickedRoleId }),
                        })
                      }
                    >
                      Create and add
                    </Button>
                    <Button
                      variant='ghost'
                      size='sm'
                      className='h-7 text-xs'
                      onClick={() => {
                        setCreating(false)
                        setTempPassword('')
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {!isSuperAdmin && !emailNotFound && !creating && (
                <p className='text-xs text-muted-foreground'>
                  Add anyone with an account. If they have none yet, you will be offered to create
                  one.
                </p>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

// Computed once — Intl.supportedValuesOf('timeZone') returns ~400 IANA zone
// names, unchanging for the life of the tab. Cast: the project's `lib`
// target (ES2020) predates this ES2022 Intl API, though every evergreen
// browser this SPA targets supports it at runtime.
const supportedTimeZones = (
  Intl as unknown as { supportedValuesOf: (key: 'timeZone') => string[] }
).supportedValuesOf('timeZone')
const TIMEZONE_OPTIONS = supportedTimeZones.map((tz) => ({
  value: tz,
  label: tz.replace(/_/g, ' '),
}))

/**
 * Edits the IANA timezone TODAY()/NOW() resolve against for this tenant's
 * calculated-field formulas. Changing an EXISTING value (not the initial
 * pick) warns before saving: recompute is silent and happens the next time
 * an in-progress submission's calculated fields are touched, not eagerly —
 * so the admin should know that up front, not be surprised by it later.
 */
function TimezoneDialog({
  tenantName,
  initial,
  pending,
  onClose,
  onSave,
}: {
  tenantName: string
  initial: string
  pending: boolean
  onClose: () => void
  onSave: (timezone: string) => void
}) {
  const [timezone, setTimezone] = useState(initial)
  const changed = timezone !== initial

  const handleSave = () => {
    if (!timezone || timezone === initial) return
    if (changed) {
      const confirmed = window.confirm(
        `Changing the timezone for "${tenantName}" will recompute in-progress submissions' ` +
          'calculated dates (e.g. TODAY()/NOW()-based fields) the next time each is edited — ' +
          'not immediately. Continue?',
      )
      if (!confirmed) return
    }
    onSave(timezone)
  }

  return (
    // modal={false}: SearchableSelect renders its dropdown in its own
    // document.body portal, not inside DialogContent's DOM subtree. Radix's
    // modal Dialog sets body { pointer-events: none } and traps focus within
    // its own content, so a modal dialog makes an externally-portalled
    // dropdown both unclickable and unfocusable — the exact bug reported
    // here (couldn't select or search). modal={false} skips both the
    // pointer-events lockdown and the focus trap; the dialog still closes on
    // Escape/outside-click via DismissableLayer, which is independent of
    // `modal`.
    <Dialog open modal={false} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='sm:max-w-sm'>
        <DialogHeader>
          <DialogTitle>Timezone — {tenantName}</DialogTitle>
          <DialogDescription>
            Used to resolve TODAY() and NOW() in this tenant&apos;s calculated-field formulas.
          </DialogDescription>
        </DialogHeader>
        <div className='flex flex-col gap-3'>
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='tenant-timezone'>Timezone</Label>
            <SearchableSelect
              value={timezone}
              onChange={setTimezone}
              options={TIMEZONE_OPTIONS}
              placeholder='Select a timezone…'
            />
          </div>
          {changed && (
            <p className='text-xs text-muted-foreground'>
              In-progress submissions&apos; date-based calculated fields will recompute the next
              time each is edited, not immediately.
            </p>
          )}
          <DialogFooter>
            <Button type='button' variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button type='button' disabled={pending || !changed} onClick={handleSave}>
              {pending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// Mirrors MAX_MAINTENANCE_TASK_OVERDUE_GRACE_DAYS in the backend DTO.
const MAX_GRACE_DAYS = 365

/**
 * Edits how many whole days a maintenance task may run past its due date before
 * it is marked overdue (z8nrz7dyc7). The days-overdue count on each row still
 * runs from the due date; grace only moves when the marker appears.
 */
function GraceDaysDialog({
  tenantName,
  initial,
  pending,
  onClose,
  onSave,
}: {
  tenantName: string
  initial: number
  pending: boolean
  onClose: () => void
  onSave: (days: number) => void
}) {
  const [raw, setRaw] = useState(String(initial))
  const days = /^\d+$/.test(raw.trim()) ? Number(raw.trim()) : NaN
  const valid = Number.isInteger(days) && days >= 0 && days <= MAX_GRACE_DAYS
  const changed = valid && days !== initial

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (changed) onSave(days)
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='sm:max-w-sm'>
        <DialogHeader>
          <DialogTitle>Maintenance task grace days: {tenantName}</DialogTitle>
          <DialogDescription>
            How many days a maintenance task can go past its due date before it is marked overdue.
            With 0, a task is overdue the day after it is due.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className='flex flex-col gap-3'>
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='tenant-grace-days'>Grace days</Label>
            <Input
              id='tenant-grace-days'
              type='number'
              inputMode='numeric'
              min={0}
              max={MAX_GRACE_DAYS}
              step={1}
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
            />
          </div>
          {!valid && (
            <p className='text-xs text-destructive'>Enter a whole number from 0 to {MAX_GRACE_DAYS}.</p>
          )}
          <p className='text-xs text-muted-foreground'>
            The number of days overdue shown on each task still counts from its due date.
          </p>
          <DialogFooter>
            <Button type='button' variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button type='submit' disabled={pending || !changed}>
              {pending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

const MIN_OFFLINE_HOURS = 1
const MAX_OFFLINE_HOURS = 24

/**
 * Edits how a phone on the mobile app works without a connection
 * (z8nrz7fh5c). An action done offline counts only if the phone reaches the
 * server within the window's hours of doing it; older ones are refused and
 * shown to the person to redo. Offline signing decides whether a phone may
 * sign actions (Start, Complete, forms) with no connection; off by default.
 */
function OfflineWindowDialog({
  tenantName,
  initial,
  initialSignatureMode,
  pending,
  onClose,
  onSave,
}: {
  tenantName: string
  initial: number
  initialSignatureMode: OfflineSignatureMode
  pending: boolean
  onClose: () => void
  onSave: (hours: number, signatureMode: OfflineSignatureMode) => void
}) {
  const [raw, setRaw] = useState(String(initial))
  const [signatureMode, setSignatureMode] = useState<OfflineSignatureMode>(initialSignatureMode)
  const hours = /^\d+$/.test(raw.trim()) ? Number(raw.trim()) : NaN
  const valid = Number.isInteger(hours) && hours >= MIN_OFFLINE_HOURS && hours <= MAX_OFFLINE_HOURS
  const changed = valid && (hours !== initial || signatureMode !== initialSignatureMode)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (changed) onSave(hours, signatureMode)
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='sm:max-w-sm'>
        <DialogHeader>
          <DialogTitle>Offline on phones: {tenantName}</DialogTitle>
          <DialogDescription>
            How long the mobile app can work without a connection. Work done offline counts only if
            the phone is back online within this many hours.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className='flex flex-col gap-3'>
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='tenant-offline-hours'>Hours</Label>
            <Input
              id='tenant-offline-hours'
              type='number'
              inputMode='numeric'
              min={MIN_OFFLINE_HOURS}
              max={MAX_OFFLINE_HOURS}
              step={1}
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
            />
          </div>
          {!valid && (
            <p className='text-xs text-destructive'>
              Enter a whole number from {MIN_OFFLINE_HOURS} to {MAX_OFFLINE_HOURS}.
            </p>
          )}
          <p className='text-xs text-muted-foreground'>12 hours is one shift.</p>
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='tenant-offline-signing'>Offline signing on phones</Label>
            <Select value={signatureMode} onValueChange={(v) => setSignatureMode(v as OfflineSignatureMode)}>
              <SelectTrigger id='tenant-offline-signing'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='OFF'>Off</SelectItem>
                <SelectItem value='ON'>On</SelectItem>
              </SelectContent>
            </Select>
            <p className='text-xs text-muted-foreground'>
              On: a phone with no connection can sign a task start, a completion and a form with the
              person&apos;s password. Off: those wait for a connection.
            </p>
          </div>
          <DialogFooter>
            <Button type='button' variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button type='submit' disabled={pending || !changed}>
              {pending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function RenameDialog({
  title,
  initial,
  pending,
  onClose,
  onSave,
}: {
  title: string
  initial: string
  pending: boolean
  onClose: () => void
  onSave: (name: string) => void
}) {
  const [name, setName] = useState(initial)
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='sm:max-w-sm'>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form
          className='flex flex-col gap-3'
          onSubmit={(e: FormEvent) => {
            e.preventDefault()
            if (name.trim()) onSave(name.trim())
          }}
        >
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='rename-name'>Name</Label>
            <Input
              id='rename-name'
              value={name}
              autoFocus
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type='button' variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button type='submit' disabled={pending || !name.trim()}>
              {pending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function NewTenantDialog({
  instanceName,
  pending,
  onClose,
  onSave,
}: {
  instanceName: string | null
  pending: boolean
  onClose: () => void
  onSave: (name: string) => void
}) {
  const [name, setName] = useState('')
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='sm:max-w-sm'>
        <DialogHeader>
          <DialogTitle>New tenant</DialogTitle>
          <DialogDescription>
            A tenant is a hospital on this instance{instanceName && <> of {instanceName}</>}.
          </DialogDescription>
        </DialogHeader>
        <form
          className='flex flex-col gap-3'
          onSubmit={(e: FormEvent) => {
            e.preventDefault()
            if (name.trim()) onSave(name.trim())
          }}
        >
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='new-tenant-name'>Tenant name</Label>
            <Input
              id='new-tenant-name'
              value={name}
              autoFocus
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type='button' variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button type='submit' disabled={pending || !name.trim()}>
              {pending ? 'Creating…' : 'Create tenant'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
